import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  TRACK_EVENTS,
} from "core/consts";
import { FileHISStateful, FileState, QueryProgress } from "core/types";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "ds/ui";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { isFileForm } from "dsl/ecosystems/PatientFiles/utils";
import { useIsScrollable } from "dsl/hooks";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { ImportIcon, UploadIcon } from "lucide-react";
import { useState } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { FilesTable } from "../FilesTable";

export function HISFileUploadScreen({
  availableFiles,
  getFilesHISProgress,
  mutationProgress,
  setAction,
  unavailableFiles,
}: {
  availableFiles: FileState[] | null;
  getFilesHISProgress: QueryProgress;
  mutationProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
  unavailableFiles: FileHISStateful[] | null;
}) {
  const [filesSelected, setFilesSelected] = useState<FileState[]>([]);

  const { isScrollable, ref: modalBodyRef } = useIsScrollable<HTMLDivElement>([
    availableFiles,
    unavailableFiles,
    getFilesHISProgress,
  ]);

  const translations = useTranslations();
  const { trackEvent } = useTracking();

  function handleClickSelectFromComputer() {
    trackEvent({
      file_implementation: "HIS",
      modal: FILE_TABLE_ACTION_UPLOAD_HIS,
      name: TRACK_EVENTS.SELECT_FROM_COMPUTER_CLICKED,
    });
    setAction({
      data: {} as FileFeatureData,
      type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
    });
  }

  function handleUploadFile() {
    trackEvent({
      file_implementation: "HIS",
      modal: FILE_TABLE_ACTION_UPLOAD_HIS,
      name: TRACK_EVENTS.UPLOAD_FILE_CLICKED,
    });

    const hisFiles = filesSelected.filter((file) => isFileHISStateful(file));
    const formFiles = filesSelected.filter((file) => isFileForm(file));

    if (formFiles.length > 0) {
      setAction({
        data: formFiles,
        type: FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
      });
    }

    if (hisFiles.length > 0) {
      setAction({
        data: hisFiles,
        type: FILE_TABLE_ACTION_UPLOAD_HIS,
      });
    }

    setFilesSelected([]);
  }

  return (
    <ModalContent>
      <ModalHeader
        as="h1"
        className="border-b border-divider pb-4 !text-heading-xl font-bold"
        id="his-file-upload-screen-title"
      >
        {translations.fileHisUploadComponent.titleNew}
      </ModalHeader>
      <ModalBody className="!p-6">
        <div ref={modalBodyRef} className="flex flex-col gap-6">
          <FilesTable
            data={availableFiles}
            description={
              translations.fileSection.fileUploadComponent
                .descriptionAvailableFiles
            }
            filesSelected={filesSelected}
            queryProgress={getFilesHISProgress}
            setFilesSelected={setFilesSelected}
            title={
              translations.extractDocs.fileUploadModal.filesAvailableForImport
            }
          />
          <FilesTable
            data={unavailableFiles}
            description={
              translations.fileSection.fileUploadComponent
                .descriptionUnavailableFiles
            }
            filesSelected={null}
            queryProgress={getFilesHISProgress}
            setFilesSelected={null}
            title={
              translations.fileSection.fileListComponent.titleFilesUnavailable
            }
          />
        </div>
      </ModalBody>
      <ModalFooter
        className={clsx("flex w-full !justify-between !p-6", {
          "relative !shadow-2xl": isScrollable,
        })}
      >
        <Button
          color="primary"
          id="select_file_from_computer"
          size="lg"
          startContent={<UploadIcon />}
          variant="ghost"
          onPress={handleClickSelectFromComputer}
        >
          {translations.fileSection.buttonUploadFromComputer}
        </Button>
        <Button
          color="primary"
          id="upload_his"
          isDisabled={!filesSelected.length}
          isLoading={isLoading(mutationProgress)}
          size="lg"
          startContent={<ImportIcon />}
          onPress={handleUploadFile}
        >
          {translations.fileSection.buttonImport}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}
